import { richTextFromMarkdown } from '@contentful/rich-text-from-markdown'
import debounce from 'lodash/debounce'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { Box } from '@components/box'
import { Column, Container, Row } from '@components/layout/grid'
import { Policy } from '@components/policy'
import type { PolicySectionsType } from '@components/policy-sections'
import { PolicySections } from '@components/policy-sections'
import PolicySidebar from '@components/policy-sidebar/policy-sidebar'
import TableOfContents from '@components/table-of-contents/table-of-contents'
import { PageMeta } from '@contentful-components/page-meta'
import { RichText } from '@contentful-components/rich-text'
import type { DocumentWithLinks } from '@contentful-components/rich-text/render'
import { getOrCreateContentfulClient } from '@graphql/client'
import { firstFromCollection } from '@graphql/collection'
import type { PageMetaFragment } from '@graphql/fragments/PageMetaFragment.graphql'
import type { PolicyCategoryFragment } from '@graphql/fragments/PolicyCategoryFragment.graphql'
import type { PolicyFragment } from '@graphql/fragments/PolicyFragment.graphql'
import { getPageMeta } from '@graphql/hooks/pageMeta'
import { getPolicies, getPolicyBySlug } from '@graphql/hooks/policies'
import { useIsMobile } from '@lib/hooks/useMedia'
import { useScroll } from '@lib/hooks/useScroll'
import { DEFAULT_PAGE_TTL } from '@lib/static/static-page'
import type { PageWithContext } from '@lib/utils/context'
import { compact } from '@lib/utils/omit'
import {
    getEnrichedPolicySections,
    policyMarkdownToRichText,
    processRichTextNodes,
} from '@lib/utils/policy'
import { isPrd } from '@shared/util/env'
import { LINK } from '@shared/util/link'

const PolicyPage: PageWithContext<Props> = ({
    policy,
    meta,
    source,
    sections,
    categories,
}) => {
    const { ref, inView } = useInView()
    const isMobile = useIsMobile()
    const [showSideBarTOC, setShowSidebarTOC] = useState(false)
    const [mounted, setMounted] = useState(false)

    // Ensure component is mounted before using client-side features
    useEffect(() => {
        setMounted(true)
    }, [])

    // Memoize the debounced function to avoid recreating it on every render
    const updateSidebarTOC = useMemo(
        () =>
            debounce(() => {
                if (!mounted) return
                if (!isMobile && !inView) {
                    setShowSidebarTOC(true)
                } else {
                    setShowSidebarTOC(false)
                }
            }, 250),
        [isMobile, inView, mounted],
    )

    // Use the scroll hook with the update callback
    useScroll(updateSidebarTOC)

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            updateSidebarTOC.cancel()
        }
    }, [updateSidebarTOC])

    const { title = '', ...rest } = policy ?? {}

    return (
        <Box pad={['vertical']}>
            {meta?.title && (
                <PageMeta id={meta.sys.id} name={title} meta={meta} />
            )}
            <Container>
                <Row>
                    <Column span={{ _: 12, lg: 8 }}>
                        <Policy title={title ?? ''} {...rest}>
                            <div ref={ref}>
                                <TableOfContents
                                    fixOnScroll={false}
                                    sections={sections}
                                />
                            </div>
                            {source && <RichText source={source} />}
                            <PolicySections sections={sections} />
                        </Policy>
                    </Column>
                    <Column span={{ _: 12, lg: 4 }}>
                        <PolicySidebar categories={categories} />
                        {showSideBarTOC && (
                            <TableOfContents
                                title={title}
                                fixOnScroll
                                sections={sections}
                                variant='sidebar'
                            />
                        )}
                    </Column>
                </Row>
            </Container>
        </Box>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const policieCategories = await getPolicies(
        getOrCreateContentfulClient('/policies'),
    )

    const paths =
        policieCategories?.data?.policyCategoryCollection?.items
            .map((categories) => categories.policies?.items)
            .flat()
            .filter(
                (policy) =>
                    !policy?.externalLink &&
                    !(
                        policy?.body?.type === 'PolicyAttachment' &&
                        policy?.body?.attachment?.contentType ===
                            'application/pdf'
                    ),
            )
            .map((policy) => (policy?.slug ? `/policies/${policy?.slug}` : ''))
            .filter(Boolean) ?? []

    return {
        paths: isPrd() ? paths : [],
        fallback: 'blocking',
    }
}

export const getStaticProps: GetStaticProps = async (context) => {
    const { slug } = context.params as { slug?: string }

    if (!slug) {
        return { notFound: true, props: undefined }
    }

    const graphQLClient = getOrCreateContentfulClient(`/policies/${slug}`)

    const [policy, sidebarCategories] = await Promise.all([
        getPolicyBySlug(graphQLClient, {
            variables: {
                slug,
            },
        }),
        getPolicies(graphQLClient),
    ])

    const item = firstFromCollection(policy?.data?.policyCollection, null)
    const categories =
        sidebarCategories?.data?.policyCategoryCollection?.items ?? []

    if (!item) {
        return {
            redirect: {
                destination: '/policies',
                permanent: false,
            },
            props: undefined,
        }
    }

    // fetch the meta data once we have the page
    const metaData = await getPageMeta(graphQLClient, {
        variables: {
            id: item?.meta?.sys.id ?? '',
        },
    })

    const { externalLink, body, sections } = item

    // fetch all the markdown and rich text sections for the policy
    const response = await getEnrichedPolicySections(
        graphQLClient,
        sections?.items ?? [],
    )

    // if policy has external link, redirect to that link
    if (externalLink) {
        return {
            props: {},
            redirect: {
                permanent: false,
                destination: externalLink,
            },
        }
    }

    if (body?.type === 'Text') {
        const compactedSource = compact(
            body?.content ?? {},
            true,
        ) as DocumentWithLinks

        // Process the rich text content for symbol and superscript replacements
        if (compactedSource?.json) {
            processRichTextNodes(compactedSource.json)
        }

        return {
            props: {
                policy: item,
                meta: metaData.data.meta,
                source: compactedSource,
                sections: response.sections ?? [],
                categories,
            },
            revalidate: DEFAULT_PAGE_TTL,
        }
    }

    if (body?.type === 'PolicyContent') {
        if (!body?.body) {
            return { notFound: true }
        }

        const json = await policyMarkdownToRichText(body?.body)

        return {
            props: {
                policy: item,
                meta: metaData.data.meta,
                source: {
                    json,
                },
                sections: response.sections ?? [],
                categories,
            },
            revalidate: DEFAULT_PAGE_TTL,
        }
    }

    if (!body && response.sections.length > 0) {
        return {
            props: {
                policy: item,
                meta: metaData.data.meta,
                source: null,
                sections: response.sections ?? [],
                categories,
            },
            revalidate: DEFAULT_PAGE_TTL,
        }
    }

    if (!body?.attachment?.url) {
        return { notFound: true }
    }

    // if the content is markdown, we just need to render it the same as PolicyContent
    if (body?.attachment.contentType === 'text/markdown') {
        // if the policy content is an attachment, we need to download the actual content here
        const json = await fetch(body?.attachment.url)
            .then((t) => t.text())
            .then(richTextFromMarkdown)

        // Process the rich text content for symbol and superscript replacements
        if (json) {
            processRichTextNodes(json)
        }

        return {
            props: {
                policy: item,
                meta: metaData.data.meta,
                source: compact(json ?? {}, true),
                sections: response.sections ?? [],
                categories,
            },
            revalidate: DEFAULT_PAGE_TTL,
        }
    }

    return {
        props: {},
        redirect: {
            permanent: false,
            destination: LINK.policyAttachment(slug).serialize(),
        },
    }
}

type Props = {
    policy: PolicyFragment
    meta: PageMetaFragment
    source?: DocumentWithLinks
    sections?: PolicySectionsType
    categories?: PolicyCategoryFragment[]
}

export default PolicyPage
